import { scrubRynReplayUrl } from '@ups/xplat/base/ryn-replay';

/**
 * Core Web Vitals / paint timing capture for Ryn Replay (#32052).
 *
 * Observers are installed for the life of a recording session and always torn
 * down on stop. Handlers never throw into the host app.
 */

export const RYN_REPLAY_PERFORMANCE_EVENT_TYPE = 'Performance';

export type RynReplayPerformanceMetricId =
  'fp' | 'fcp' | 'fid' | 'lcp' | 'cls' | 'longtask';

/**
 * Shortest main-thread task the recorder keeps (WI 37214). The browser reports
 * every task over 50 ms; only the ones a user feels as a stuck page are worth a
 * batch line, and the nightly sweep files a "not responding" finding from them.
 */
export const RYN_REPLAY_LONG_TASK_MIN_MS = 500;

export type RynReplayPerformanceUnit = 'ms' | 'score';

export interface IRynReplayPerformanceMetricPayload {
  metric: RynReplayPerformanceMetricId;
  value: number;
  unit: RynReplayPerformanceUnit;
  pageUrl: string;
}

export type RynReplayPerformanceEmit = (
  type: string,
  payload: Record<string, unknown>
) => void;

export interface IRynReplayPerformanceCapture {
  install(): void;
  uninstall(): void;
}

export const paintEntryNameToMetric = (
  name: string
): RynReplayPerformanceMetricId | undefined => {
  if (name === 'first-paint') {
    return 'fp';
  }
  if (name === 'first-contentful-paint') {
    return 'fcp';
  }
  return undefined;
};

type PerformanceObserverCallback = (list: PerformanceObserverEntryList) => void;

interface PerformanceObserverLike {
  observe: (options: PerformanceObserverInit) => void;
  disconnect: () => void;
}

type PerformanceObserverCtor = new (
  callback: PerformanceObserverCallback
) => PerformanceObserverLike;

export class RynReplayPerformanceCapture implements IRynReplayPerformanceCapture {
  private installed = false;
  private observers: PerformanceObserverLike[] = [];
  private clsScore = 0;
  private readonly oneShotEmitted = new Set<RynReplayPerformanceMetricId>();

  constructor(
    private readonly emit: RynReplayPerformanceEmit,
    private readonly win: Window = globalThis as unknown as Window
  ) {}

  install(): void {
    if (this.installed) {
      return;
    }
    this.installed = true;
    this.captureExistingPaint();
    this.observePaint();
    this.observeLcp();
    this.observeCls();
    this.observeFid();
    this.observeLongTasks();
  }

  uninstall(): void {
    if (!this.installed) {
      return;
    }
    this.installed = false;
    for (const observer of this.observers) {
      try {
        observer.disconnect();
      } catch {
        // never break the host
      }
    }
    this.observers = [];
    this.clsScore = 0;
    this.oneShotEmitted.clear();
  }

  private pageUrl(): string {
    const href = this.win.location?.href ?? '';
    return href ? scrubRynReplayUrl(href) : '';
  }

  private emitMetric(
    metric: RynReplayPerformanceMetricId,
    value: number,
    unit: RynReplayPerformanceUnit,
    repeatable = false
  ): void {
    if (!repeatable && this.oneShotEmitted.has(metric)) {
      return;
    }
    if (!repeatable) {
      this.oneShotEmitted.add(metric);
    }
    try {
      this.emit(RYN_REPLAY_PERFORMANCE_EVENT_TYPE, {
        metric,
        value,
        unit,
        pageUrl: this.pageUrl(),
      });
    } catch {
      // never break the host
    }
  }

  private captureExistingPaint(): void {
    try {
      const entries = this.win.performance?.getEntriesByType?.('paint') ?? [];
      for (const entry of entries) {
        const metric = paintEntryNameToMetric(
          (entry as PerformanceEntry).name ?? ''
        );
        if (
          metric &&
          typeof (entry as PerformanceEntry).startTime === 'number'
        ) {
          this.emitMetric(metric, (entry as PerformanceEntry).startTime, 'ms');
        }
      }
    } catch {
      // never break the host
    }
  }

  private observePaint(): void {
    this.observe('paint', (entries) => {
      for (const entry of entries) {
        const metric = paintEntryNameToMetric(entry.name ?? '');
        if (metric && typeof entry.startTime === 'number') {
          this.emitMetric(metric, entry.startTime, 'ms');
        }
      }
    });
  }

  private observeLcp(): void {
    this.observe('largest-contentful-paint', (entries) => {
      for (const entry of entries) {
        if (typeof entry.startTime === 'number') {
          this.emitMetric('lcp', entry.startTime, 'ms', true);
        }
      }
    });
  }

  private observeCls(): void {
    this.observe('layout-shift', (entries) => {
      for (const entry of entries) {
        const shift = entry as PerformanceEntry & {
          value?: number;
          hadRecentInput?: boolean;
        };
        if (shift.hadRecentInput || typeof shift.value !== 'number') {
          continue;
        }
        this.clsScore += shift.value;
        this.emitMetric('cls', this.clsScore, 'score', true);
      }
    });
  }

  private observeFid(): void {
    this.observe('first-input', (entries) => {
      for (const entry of entries) {
        const input = entry as PerformanceEntry & {
          processingStart?: number;
        };
        if (
          typeof input.startTime === 'number' &&
          typeof input.processingStart === 'number'
        ) {
          this.emitMetric('fid', input.processingStart - input.startTime, 'ms');
        }
      }
    });
  }

  private observeLongTasks(): void {
    this.observe('longtask', (entries) => {
      for (const entry of entries) {
        if (
          typeof entry.duration === 'number' &&
          entry.duration >= RYN_REPLAY_LONG_TASK_MIN_MS
        ) {
          this.emitMetric('longtask', entry.duration, 'ms', true);
        }
      }
    });
  }

  private observe(
    type: string,
    onEntries: (entries: PerformanceEntry[]) => void
  ): void {
    const ctor = (
      this.win as Window & { PerformanceObserver?: PerformanceObserverCtor }
    ).PerformanceObserver;
    if (!ctor) {
      return;
    }
    try {
      const observer = new ctor((list) => {
        try {
          onEntries(list.getEntries() as PerformanceEntry[]);
        } catch {
          // never break the host
        }
      });
      observer.observe({
        type,
        buffered: true,
      } as PerformanceObserverInit);
      this.observers.push(observer);
    } catch {
      // unsupported entry type in this browser — skip quietly
    }
  }
}
